import { http } from './axios';
import merge from 'lodash/merge';
import { STATES } from '@constants/SESSION';
export const ReportingConfigurationAPI = {
  getReportingTypeOptions: () => http.get('/email-reports-types'),
  getReportConfigurations: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/report-configurations`),
  createReport: ({ body, workflowId }) =>
    http.post(`/workflows/${workflowId}/report-configurations`, body),
  updateReport: ({ body, workflowId, report_config_id }) =>
    http.patch(
      `/workflows/${workflowId}/report-configurations/${report_config_id}`,
      body,
    ),
  deleteReport: ({ workflowId, report_config_id }) =>
    http.delete(
      `/workflows/${workflowId}/report-configurations/${report_config_id}`,
    ),
};

export const Users = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/users',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getOne: (email) => http.get(`/users/${email}`),
  create: (values) => http.post('/users', values),
  update: ({ email, values }) => http.patch(`/users/${email}`, values),
  delete: (email) => http.delete(`/users/${email}`),
  forget: (email) =>
    http.patch(`/users/${email}`, {
      forget: true,
    }),
  verify: (data) => http.post(`mfa/backoffice/verify`, data),
  resetPassword: (email, data) =>
    http.patch(`/user/${email}/reset-password?error=false`, data),
};

export const UsersByRole = {
  getAll: (role) => http.get(`/users?filter[role]=${role}`),
};

export const Password = {
  request: (values) => http.post('/password/request', values),
  reset: (values) => http.post('/password/reset', values),
};

export const Groups = {
  // get all the available groups
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/groups',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  // get the specific group's info
  getOne: (groupId) => http.get(`/groups/${groupId}`),
  // create a group with a given admin
  create: (values) => http.post('/groups', values),
  // update a group
  update: ({ groupId, values }) => http.patch(`/groups/${groupId}`, values),
  // delete a group
  delete: (groupId) => http.delete(`/groups/${groupId}`),
  // add users by their id into the group
  updateUsers: ({ groupId, values }) =>
    http.put(`/groups/${groupId}/users`, values),
  // delete users from the group by their id
  deleteUsers: ({ groupId, values }) =>
    http.request({
      data: values,
      method: 'delete',
      url: `/groups/${groupId}/users`,
    }),
  // get all the users inside the group
  getAllUsers: ({ groupId, pagination = true, configuration = {} }) =>
    http.get(
      `/groups/${groupId}/users`,
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  // add users by their id into the group
  updateAdmins: ({ groupId, values }) =>
    http.put(`/groups/${groupId}/admins`, values),
  // delete users from the group by their id
  deleteAdmins: ({ groupId, values }) =>
    http.request({
      data: values,
      method: 'delete',
      url: `/groups/${groupId}/admins`,
    }),
  // get all the users inside the group
  getAllAdmins: ({ groupId, pagination = true, configuration = {} }) =>
    http.get(
      `/groups/${groupId}/admins`,
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const Roles = {
  getAll: () => http.get('/roles'),
  getUsersByRole: (role) => http.get(`/roles/${role}/users`),
  getPermissionsByRole: (role) => http.get(`/roles/${role}/permissions`),
  getPermissions: () => http.get('/permissions'),
};

export const IdentificationModes = {
  get: () => http.get(`/identification_modes`),
  getEnabledModes: (workflowId, stepId) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/identification_modes`),
  getEnabledModeContent: (workflowId, stepId, identification_mode) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/identification_modes/${identification_mode}/contents`,
    ),
  createEnabledMode: (workflowId, stepId, data) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/identification_modes`,
      data,
    ),
  updateEnabledMode: (workflowId, stepId, identification_mode, data) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/identification_modes/${identification_mode}`,
      data,
    ),
  updateEnabledModeContent: (workflowId, stepId, identification_mode, data) =>
    http.put(
      `/workflows/${workflowId}/steps/${stepId}/identification_modes/${identification_mode}/contents`,
      data,
    ),
  deleteEnabledMode: (workflowId, stepId, identification_mode) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/identification_modes/${identification_mode}`,
    ),
};

export const Tenants = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/tenants',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getAllSearch: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/tenants/search',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getOne: (tenantId) => http.get(`/tenants/${tenantId}`),
  create: (values) => http.post('/tenants', values),
  update: ({ tenantId, values }) => http.patch(`/tenants/${tenantId}`, values),
  delete: (tenantId) => http.delete(`/tenants/${tenantId}`),
};

export const WorkflowTypes = {
  getAll: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/workflow_types',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getFilter: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/filters/workflow_types',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const WebhookTypes = {
  getAll: () => http.get('/workflow_webhook_types'),
};

export const Languages = {
  getAll: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/languages',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getFilter: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/filters/languages',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const SystemLanguages = {
  getAll: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/languages/system',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const EmailTypes = {
  getAll: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/email_types',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const SmsTypes = {
  getAll: ({ pagination = false, configuration = {} }) =>
    http.get(
      '/sms_types',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const NotificationTypes = {
  getAll: () => http.get('/notification_types'),
};

export const Workflows = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/workflows',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getOne: (workflowId) => http.get(`/workflows/${workflowId}`),
  getAuthDrivers: () => http.get('/workflow_authentication_drivers'),
  getFilter: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/filters/workflows',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  create: (values) => http.post('/workflows', values),
  update: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}`, values),
  delete: (workflowId) => http.delete(`/workflows/${workflowId}`),
  export: (workflowId) =>
    http.get(`/workflows-export/${workflowId}`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/json',
      },
    }),
  import: (data) => http.post(`/workflows-import`, data),
  compare: (workflowId, data) =>
    http.post(`/workflows-compare/${workflowId}`, data),
};

export const PublicWorkflows = {
  getAll: () => http.get('/public/workflows'),
  getOne: (workflowName) => http.get(`/public/workflows/${workflowName}`),
  update: ({ workflowName, values }) =>
    http.post(`/public/workflows/${workflowName}`, values),
  delete: (workflowName) => http.delete(`/public/workflows/${workflowName}`),
};

export const Partitions = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/partitions',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getOne: (partitionId) => http.get(`/partitions/${partitionId}`),
  create: (values) => http.post('/partitions', values),
  update: ({ partitionId, values }) =>
    http.patch(`/partitions/${partitionId}`, values),
  delete: (partitionId) => http.delete(`/partitions/${partitionId}`),
  getPartitionSession: (partitionId) =>
    http.get(`/partitions/${partitionId}/sessions`),
  putPartitionSession: (partitionId, sessions) =>
    http.put(`/partitions/${partitionId}/sessions`, { session_ids: sessions }),
  deletePartitionSession: (partitionId, sessions) =>
    http.delete(`/partitions/${partitionId}/sessions`, {
      data: { session_ids: sessions },
    }),
  getPartitionUsers: (partitionId) =>
    http.get(`/partitions/${partitionId}/users`),
  putPartitionUsers: (partitionId, ids) =>
    http.put(`/partitions/${partitionId}/users`, { user_ids: ids }),
  deletePartitionUsers: (partitionId, ids) =>
    http.delete(`/partitions/${partitionId}/users`, {
      data: { user_ids: ids },
    }),
};

export const SignSummaryPDFServices = {
  getAll: () => http.get('/sign_summary_pdf_services'),
  getSignService: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/sign_summary_pdf_service`),
  postConfiguration: ({ workflowId, values }) =>
    http.put(`workflows/${workflowId}/sign_summary_pdf_service`, values),
  deleteConfiguration: ({ workflowId }) =>
    http.delete(`workflows/${workflowId}/sign_summary_pdf_service`),
};

export const ThirdPartySessionCreationService = {
  getServices: () => http.get('/third_party_session_creation_services'),
  getServiceConfiguration: (workflow) =>
    http.get(
      `/workflows/${workflow}/third_party_session_creation_service_configuration`,
    ),
  createServiceConfiguration: (workflow, data) =>
    http.post(
      `/workflows/${workflow}/third_party_session_creation_service_configuration`,
      data,
    ),
  deleteServiceConfiguration: (workflow) =>
    http.delete(
      `/workflows/${workflow}/third_party_session_creation_service_configuration`,
    ),
  download: (workflow, attachmentId) =>
    http.get(
      `/workflows/${workflow}/third_party_session_creation_service_configuration/${attachmentId}/download`,
    ),
};

export const WorkflowEventsService = {
  getAll: (configuration = {}) =>
    http.get(
      `/workflows/events`,
      merge(configuration, {
        headers: { 'X-Pagination': true },
      }),
    ),
};

export const TroubleshootingService = {
  getAll: (configuration) => {
    return http.get(
      '/sessions/activities',
      merge(configuration, {
        headers: { 'X-Pagination': true },
      }),
    );
  },
  reject: ({ sessionId }) => {
    return http.get(`/sessions/${sessionId}/reject`);
  },
  getSessionWebhooks: ({ sessionId }) => {
    return http.get(`/sessions/${sessionId}/webhookStates`);
  },
  resendWebhook: ({ sessionId, state }) => {
    return http.post(`/sessions/${sessionId}/sendWebhook`, {
      state,
    });
  },
  reset: ({ sessionId }) => http.get(`/sessions/${sessionId}/reset`),
};

export const ClientReportsService = {
  getReport: ({ year, type, workflowId }) =>
    http.get(`/reports?year=${year}&type=${type}&workflow_id=${workflowId}`),
  exportReport: ({ year, type, workflowId }) =>
    http.get(
      `/reports/export?year=${year}&type=${type}${
        workflowId ? '&workflow_id=' + workflowId : ''
      }`,
      {
        responseType: 'blob',
      },
    ),
};

export const ReasonsService = {
  getReasons: () => http.get(`/workflows/session_pause_and_reschedule_reasons`),
  getCurrentWorkflowReasons: ({ id }) =>
    http.get(`/workflows/${id}/session_pause_and_reschedule_reasons`),
  updateCurrentWorkflowReasons: ({ id, body }) =>
    http.put(`/workflows/${id}/session_pause_and_reschedule_reasons`, body),
  deleteCurrentWorkflowReasons: ({ id, body }) =>
    http.delete(`/workflows/${id}/session_pause_and_reschedule_reasons`, body),
};

export const Sessions = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/sessions',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getList: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/sessions/list',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getOne: (sessionId) => http.get(`/sessions/${sessionId}`),
  getInfo: (sessionId) => http.get(`/sessions/${sessionId}/info`),
  getSummary: (sessionId) => http.get(`/sessions/${sessionId}/summary`),
  joinWebrtcCall: (sessionId) =>
    http.post(`/sessions/${sessionId}/webrtc/join`, {}),
  getWebrtcCallStatus: (sessionId) =>
    http.get(`/sessions/${sessionId}/webrtc/status`),
  sendWebrtcMessage: (sessionId, message) =>
    http.post(`/sessions/${sessionId}/webrtc/messages`, message),
  create: (values) => http.post('/sessions', values),
  createBulk: (data) => http.post('/sessions/import', data),
  update: ({ sessionId, values }) =>
    http.patch(`/sessions/${sessionId}`, values),
  updateMassiveActions: (sessions) =>
    http.post('/sessions/massive_actions', sessions),
  updateFormData: ({ sessionId, values }) =>
    http.post(`/sessions/${sessionId}`, values),
  delete: (sessionId) => http.delete(`/sessions/${sessionId}`),
  getRejectReasons: () => http.get(`/sessions/asset_reject_reasons`),
  getAllRejectReasons: () => http.get('/workflows/session_reject_reasons'),
  getCustomRejectReasons: (workflowId) =>
    http.get(`/workflows/${workflowId}/session_reject_reasons`),
  putCustomRejectReasons: ({ states, workflowId }) =>
    http.put(`/workflows/${workflowId}/session_reject_reasons`, {
      codes: states,
    }),
  deleteRejectReasons: ({ states, workflowId }) =>
    http.delete(`/workflows/${workflowId}/session_reject_reasons`, {
      data: { codes: states },
    }),
  getAcceptReasons: () => http.get(`/sessions/asset_accept_reasons`),
  getAllAcceptReasons: () => http.get('/workflows/session_accept_reasons'),
  getCustomAcceptReasons: (workflowId) =>
    http.get(`/workflows/${workflowId}/session_accept_reasons`),
  putCustomAcceptReasons: ({ states, workflowId }) =>
    http.put(`/workflows/${workflowId}/session_accept_reasons`, {
      codes: states,
    }),
  deleteAcceptReasons: ({ states, workflowId }) =>
    http.delete(`/workflows/${workflowId}/session_accept_reasons`, {
      data: { codes: states },
    }),
  sendResetEmail: ({ sessionId, type }) =>
    http.post(`/sessions/${sessionId}/email/${type}`),
  finalize: (sessionId, operatorId, orderNumber, firstName, lastName) =>
    http.patch(`/sessions/${sessionId}`, {
      id: sessionId,
      state: STATES.TO_BE_VALIDATED,
      operator_id: operatorId,
      order_number: orderNumber,
      first_name: firstName,
      last_name: lastName,
    }),
  getRemainingStep: (sessionId) =>
    http.get(`/sessions/${sessionId}/remaining_steps`),
  getAddresses: (sessionId) => http.get(`/sessions/${sessionId}/addresses`),
  addAddresses: ({ sessionId, data }) =>
    http.post(`/sessions/${sessionId}/addresses`, data),
  deleteAddress: ({ sessionId, addressId }) =>
    http.delete(`/sessions/${sessionId}/addresses/${addressId}`),
  updateAddress: ({ sessionId, addressId, data }) =>
    http.patch(`/sessions/${sessionId}/addresses/${addressId}`, data),
  refreshRiskLevel: ({ sessionId }) =>
    http.get(`/sessions/${sessionId}/risk_rating`),
  export: (sessions) => http.post('/sessions/export', sessions),
  getEmailEvents: (sessionId) =>
    http.get(`/sessions/${sessionId}/email_callback_events`),
  getJourneyEvents: ({ sessionId, pagination = true, configuration = {} }) =>
    http.get(
      `/sessions/${sessionId}/journey-events`,
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  getIdentityVerificationReport: (sessionId) =>
    http.get(`/sessions/${sessionId}/identity-verification-report`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf',
      },
    }),
};

export const SessionUserInformation = {
  get: (sessionId) => http.get(`sessions/${sessionId}/user_information`),
  getSpid: (sessionId) =>
    http.get(`sessions/${sessionId}/spid_user_information`),
  update: ({ sessionId, values }) =>
    http.put(`sessions/${sessionId}/user_information`, values),
};

export const SessionsStepConfirmation = {
  update: ({ sessionId, stepId }) =>
    http.patch(`/sessions/${sessionId}/step_confirmation/${stepId}`),
};

export const SessionsReports = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/sessions/reports',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  export: (values, configuration) =>
    http.post('/sessions/reports/export', values, merge(configuration)),
};

export const SessionsReportsActivity = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/sessions/reports/activity',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  export: (values, configuration) =>
    http.post(
      '/sessions/reports/export_activity',
      values,
      merge(configuration),
    ),
};

export const BillingReports = {
  getServices: () => http.get('/workflows/services'),
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      '/workflows/service_usage/report',
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  export: ({ configuration = {} }) =>
    http.get(
      '/workflows/service_usage/export',
      merge(configuration, { responseType: 'blob' }),
    ),
};

export const SsoConfiguration = {
  getProfilesList: () => http.get('sso-profiles'),
  postProfile: (data) => http.post('sso-profiles', data),
  updateProfile: (id, data) => http.patch(`sso-profiles/${id}`, data),
  getProfile: (id) => http.get(`sso-profiles/${id}`),
  getServiceConfig: () => http.get('sso_services_configurations'),
  deleteProfile: (id) => http.delete(`sso-profiles/${id}`),
  getServices: () => http.get('sso/services'),
  getAuthUrl: (sso_service) => http.post('sso/authorize-url', { sso_service }),
  getToken: ({ sso_service, code, state }) =>
    http.post('sso/token', { sso_service, code, state }),
};

export const GenericSteps = {
  getGenericSteps: (theme) =>
    http.get(`workflow_type_themes/${theme}/generic_steps`),
  getAll: (id) => http.get(`/generic_steps?filter[workflow_type_id]=${id}`),
};

export const WorkflowSteps = {
  getAll: (workflowId) => http.get(`/workflows/${workflowId}/steps`),
  getOne: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/steps`, values),
  update: ({ workflowId, stepId, values }) =>
    http.patch(`/workflows/${workflowId}/steps/${stepId}`, values),
  delete: ({ workflowId, stepId }) =>
    http.delete(`/workflows/${workflowId}/steps/${stepId}`),
};

export const WorkflowStepLanguages = {
  getAll: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/languages`),
  getOne: ({ workflowId, stepId, languageId }) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/languages/${languageId}`,
    ),
  create: ({ workflowId, stepId, values }) =>
    http.post(`/workflows/${workflowId}/steps/${stepId}/languages`, values),
  update: ({ workflowId, stepId, languageId, values }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/languages/${languageId}`,
      values,
    ),
  delete: ({ workflowId, stepId, languageId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/languages/${languageId}`,
    ),
};

export const WorkflowStepKeys = {
  getAll: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/keys`),
  getOne: ({ workflowId, stepId, keyId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/keys/${keyId}`),
  create: ({ workflowId, stepId, values }) =>
    http.post(`/workflows/${workflowId}/steps/${stepId}/keys`, values),

  delete: ({ workflowId, stepId, keyId }) =>
    http.delete(`/workflows/${workflowId}/steps/${stepId}/keys/${keyId}`),
};

export const WorkflowStepContents = {
  getAll: ({ workflowId, stepId, configuration }) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/contents`,
      configuration,
    ),
  getOne: ({ workflowId, stepId, contentId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/contents/${contentId}`),
  create: ({ workflowId, stepId, values }) =>
    http.post(`/workflows/${workflowId}/steps/${stepId}/contents`, values),
  update: ({ workflowId, stepId, contentId, values }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/contents/${contentId}`,
      values,
    ),
  delete: ({ workflowId, stepId, contentId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/contents/${contentId}`,
    ),
};

export const WorkflowStepConfigurations = {
  getAll: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/configurations`),
  getOne: ({ workflowId, stepId, configId }) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/configurations/${configId}`,
    ),
  create: ({ workflowId, stepId, values }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/configurations`,
      values,
    ),
  update: ({ workflowId, stepId, configId, values }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/configurations/${configId}`,
      values,
    ),
  delete: ({ workflowId, stepId, configId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/configurations/${configId}`,
    ),
};

export const WorkflowStepSessionIdTypes = {
  getAll: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/session_id_types`),
  update: ({ workflowId, stepId, values }) =>
    http.put(
      `/workflows/${workflowId}/steps/${stepId}/session_id_types`,
      values,
    ),
  delete: ({ workflowId, stepId, values }) =>
    http.request({
      data: values,
      method: 'delete',
      url: `/workflows/${workflowId}/steps/${stepId}/session_id_types`,
    }),
  postContent: ({ workflowId, stepId, data, idType }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/session_id_types/${idType}/contents`,
      data,
    ),
  updateContent: ({ workflowId, stepId, data, idType, contentId }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/session_id_types/${idType}/contents/${contentId}`,
      data,
    ),
  deleteContent: ({ workflowId, stepId, idType, contentId }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/session_id_types/${idType}/contents/${contentId}`,
    ),
};

export const Configurations = {
  getAutoConfirmKeys: () => http.get('/generic_steps/auto_confirm_keys'),
  get: ({ genericStep }) =>
    http.get(
      `/configurations/workflow_step_ml_checks?filter[generic_step]=${genericStep}`,
    ),
  getSignatureTypes: () =>
    http.get(`/configurations/workflow_idsign_signature_types`),
  getCertificateTypes: () =>
    http.get(`/configurations/workflow_idsign_certificate_types`),
};

export const WorkflowStepMlChecks = {
  get: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/ml_checks`),
  create: ({ workflowId, stepId, values }) =>
    http.post(`/workflows/${workflowId}/steps/${stepId}/ml_checks`, values),
  update: ({
    workflowId,
    stepId,
    mlcheck,
    threshold,
    configuredOnId,
    verifyWithId,
  }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/ml_checks/${mlcheck}`,
      {
        threshold,
        configured_on_workflow_step_id: configuredOnId,
        verify_with_workflow_selfie_video_record_step_id: verifyWithId,
      },
    ),
  delete: ({ workflowId, stepId, mlcheck }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/ml_checks/${mlcheck}`,
    ),
};

export const WorkflowTypeThemes = {
  getAll: ({ workflowTypeId, configuration }) =>
    http.get(
      `/workflow_type_themes?filter[workflow_type]=${workflowTypeId}`,
      configuration,
    ),
  getOne: ({ themeId }) => http.get(`/workflow_type_themes${themeId}`),
  create: ({ values }) => http.post('/workflow_type_themes', values),
  update: ({ themeId, values }) =>
    http.patch(`/workflow_type_themes${themeId}`, values),
  delete: ({ themeId }) => http.delete(`/workflow_type_themes${themeId}`),
};

export const WorkflowEmails = {
  getAll: (workflowId) => http.get(`/workflows/${workflowId}/emails`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/emails`, values),
  delete: ({ workflowId, emailId }) =>
    http.delete(`/workflows/${workflowId}/emails/${emailId}`),
  getAllAttahchments: ({ workflowId, emailId }) =>
    http.get(`/workflows/${workflowId}/emails/${emailId}/attachments`),
  addAttahchment: ({ workflowId, emailId, data }) =>
    http.post(`/workflows/${workflowId}/emails/${emailId}/attachments`, data),
  deleteAttahchment: ({ workflowId, emailId, attachmentId }) =>
    http.delete(
      `/workflows/${workflowId}/emails/${emailId}/attachments/${attachmentId}`,
    ),
  downloadAttahchment: ({ workflowId, emailId, attachmentId }) =>
    http.get(
      `/workflows/${workflowId}/emails/${emailId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' },
    ),
};

export const WorkflowEmailContents = {
  getAll: ({ workflowId, emailId }) =>
    http.get(`/workflows/${workflowId}/emails/${emailId}/contents`),
  create: ({ workflowId, emailId, values }) =>
    http.post(`/workflows/${workflowId}/emails/${emailId}/contents`, values),
  update: ({ workflowId, emailId, contentId, values }) =>
    http.patch(
      `/workflows/${workflowId}/emails/${emailId}/contents/${contentId}`,
      values,
    ),
  updateConfig: ({ workflowId, emailId, values }) =>
    http.patch(`/workflows/${workflowId}/emails/${emailId}`, values),
};

export const WorkflowSms = {
  getAll: (workflowId) => http.get(`/workflows/${workflowId}/sms`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/sms`, values),
  delete: ({ workflowId, smsId }) =>
    http.delete(`/workflows/${workflowId}/sms/${smsId}`),
};

export const WorkflowSmsContents = {
  getAll: ({ workflowId, smsId }) =>
    http.get(`/workflows/${workflowId}/sms/${smsId}/contents`),
  create: ({ workflowId, smsId, values }) =>
    http.post(`/workflows/${workflowId}/sms/${smsId}/contents`, values),
  update: ({ workflowId, smsId, contentId, values }) =>
    http.patch(
      `/workflows/${workflowId}/sms/${smsId}/contents/${contentId}`,
      values,
    ),
};

export const WorkflowWorkingHours = {
  getAll: (workflowId) => http.get(`/workflows/${workflowId}/workinghours`),
  getOne: ({ workflowId, day }) =>
    http.get(`/workflows/${workflowId}/workinghours/${day}`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/workinghours`, values),
  update: ({ workflowId, day, values }) =>
    http.patch(`/workflows/${workflowId}/workinghours/${day}`, values),
  delete: ({ workflowId, day }) =>
    http.delete(`/workflows/${workflowId}/workinghours/${day}`),
};

export const UserWorkingHours = {
  getAll: (email) => http.get(`/users/${email}/workinghours`),
  getOne: ({ email, day }) => http.get(`/users/${email}/workinghours/${day}`),
  create: ({ email, values }) =>
    http.post(`/users/${email}/workinghours`, values),
  update: ({ email, day, values }) =>
    http.patch(`/users/${email}/workinghours/${day}`, values),
  delete: ({ email, day }) =>
    http.delete(`/users/${email}/workinghours/${day}`),
};

export const UserGroupWorkingHours = {
  getAll: (groupId) => http.get(`/groups/${groupId}/workinghours`),
  getOne: ({ groupId, day }) =>
    http.get(`/groups/${groupId}/workinghours/${day}`),
  create: ({ groupId, values }) =>
    http.post(`/groups/${groupId}/workinghours`, values),
  update: ({ groupId, day, values }) =>
    http.patch(`/groups/${groupId}/workinghours/${day}`, values),
  delete: ({ groupId, day }) =>
    http.delete(`/groups/${groupId}/workinghours/${day}`),
};

export const WorkflowIDSignConfiguration = {
  getAll: (workflowId) =>
    http.get(`/workflows/${workflowId}/idsign_configuration`),
  getOne: ({ workflowId, idSign }) =>
    http.get(`/workflows/${workflowId}/idsign_configuration/${idSign}`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/idsign_configuration`, values),
  update: ({ workflowId, idSign, values }) =>
    http.patch(
      `/workflows/${workflowId}/idsign_configuration/${idSign}`,
      values,
    ),
  delete: ({ workflowId, idSign }) =>
    http.delete(`/workflows/${workflowId}/idsign_configuration/${idSign}`),
  addServiceProvider: ({ workflowId, idSign, provider }) =>
    http.put(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service`,
      { name: provider },
    ),
  deleteServiceProvider: ({ workflowId, idSign, signingPdfService }) =>
    http.delete(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service/${signingPdfService}`,
    ),
  getServiceProviders: () => http.get(`/workflow_signing_pdf_services`),
  getSelectedExternalPdfService: ({ workflowId, idSign }) =>
    http.get(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service`,
    ),
  getExternalPdfService: ({ workflowId, idSign, signingPdfService }) =>
    http.get(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service/${signingPdfService}/configuration`,
    ),
  createExternalPdfService: ({
    workflowId,
    idSign,
    signingPdfService,
    documents,
  }) =>
    http.put(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service/${signingPdfService}/configuration`,
      documents,
    ),
  deleteExternalPdfService: ({
    workflowId,
    idSign,
    signingPdfService,
    configuration,
  }) =>
    http.delete(
      `/workflows/${workflowId}/idsign_configuration/${idSign}/signing_pdf_service/${signingPdfService}/configuration/${configuration}`,
    ),
};

export const SigningServices = {
  getAll: () => http.get(`/digital_signing_services`),
  getOne: ({ workflowId }) =>
    http.get(
      `/workflows/${workflowId}/digital_signing_services_configurations`,
    ),
  createConfigurations: ({ workflowId, values }) =>
    http.put(
      `/workflows/${workflowId}/digital_signing_services_configurations`,
      values,
    ),
  deleteConfigurations: ({ workflowId }) =>
    http.delete(
      `/workflows/${workflowId}/digital_signing_services_configurations`,
    ),
  getMetaProperties: () =>
    http.get(`/digital_signing_services/keys_properties`),
  getFinalStates: () =>
    http.get(`/configurations/workflow_idsign_session_states`),
};

export const SessionAssets = {
  getAll: ({ sessionId, configuration }) =>
    http.get(`/sessions/${sessionId}/assets`, configuration),
  getOne: ({ sessionId, assetId }) =>
    http.get(`/sessions/${sessionId}/assets/${assetId}`),
  create: ({ values, sessionId }) =>
    http.post(`/sessions/${sessionId}/assets`, values),
  update: ({ sessionId, values, assetId }) =>
    http.patch(`/sessions/${sessionId}/assets/${assetId}`, values),
  updateFormData: ({ sessionId, values, assetId }) =>
    http.post(`/sessions/${sessionId}/assets/${assetId}`, values),
  delete: ({ sessionId, assetId }) =>
    http.delete(`/sessions/${sessionId}/assets/${assetId}`),
  reject: ({ sessionId, assetId, reasonKey, comment = '' }) =>
    http.post(
      `/sessions/${sessionId}/assets/${assetId}/reject`,
      comment.length > 0
        ? { reason_key: reasonKey, comment }
        : { reason_key: reasonKey },
    ),
  retain: ({ sessionId, assetId }) =>
    http.post(`/sessions/${sessionId}/assets/${assetId}/retain`),
};

export const SessionDocumentVerification = {
  get: ({ sessionId }) =>
    http.get(`/sessions/${sessionId}/document_verification`),
  preview: ({ sessionId, assetId, metaName }) =>
    http.get(
      `/sessions/${sessionId}/assets/${assetId}/document_verification/${metaName}/preview`,
      { responseType: 'blob' },
    ),
};

export const SessionMlCheck = {
  get: ({ sessionId }) => http.get(`/sessions/${sessionId}/ml_checks`),
};

export const SessionAutoValidation = {
  getAll: ({ pagination, sessionId }) =>
    http.get(
      `/sessions/${sessionId}/autovalidation_history`,
      merge({
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const WorkflowAssets = {
  getAll: ({ workflowId, configuration }) =>
    http.get(`/workflows/${workflowId}/assets`, configuration),
  getOne: ({ workflowId, assetId }) =>
    http.get(`/workflows/${workflowId}/assets/${assetId}`),
  download: ({ workflowId, assetId }) =>
    http.get(`/workflows/${workflowId}/assets/${assetId}/download`),
  create: ({ values, workflowId }) =>
    http.post(`/workflows/${workflowId}/assets`, values),
  update: ({ workflowId, values, assetId }) =>
    http.patch(`/workflows/${workflowId}/assets/${assetId}`, values),
  updateFormData: ({ workflowId, values, assetId }) =>
    http.post(`/workflows/${workflowId}/assets/${assetId}`, values),
  delete: ({ workflowId, assetId }) =>
    http.delete(`/workflows/${workflowId}/assets/${assetId}`),
};

export const SessionsLifecycle = {
  getAll: ({ pagination = true, configuration = {} }) =>
    http.get(
      `/sessions/lifecycle`,
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
};

export const SessionIdTypes = {
  getAll: () => http.get('/sessions/id_types'),
};

export const DocumentValidation = {
  getAllDocumentTypes: () => http.get('/document_validation/document_types'),
  getDocumentFields: () =>
    http.get(`/document_validation/document_type_fields`),
  getDocumentValidation: (workflowId) =>
    http.get(`/workflows/${workflowId}/document_validation
    `),
  postDocumentValidation: (workflowId, values) =>
    http.put(
      `/workflows/${workflowId}/document_validation
    `,
      values,
    ),
  deleteDocumentValidation: (workflowId, validationId) =>
    http.delete(
      `/workflows/${workflowId}/document_validation/${validationId}
    `,
    ),
};
export const ProtectedFields = {
  getAll: () => http.get(`/workflow_protected_fields`),
  getConfiguration: (workflowId) =>
    http.get(
      `/workflows/${workflowId}/workflow_protected_fields_configuration`,
    ),
  putConfiguration: (workflowId, payload) =>
    http.put(
      `/workflows/${workflowId}/workflow_protected_fields_configuration`,
      payload,
    ),
};

export const SessionAssetTypes = {
  getAll: () => http.get('/sessions/asset_types'),
  getSessionAssetTypes: ({ sessionId }) =>
    http.get(`/sessions/${sessionId}/asset_types`),
  getLimits: (workflowId) =>
    http.get(
      `/workflows/${workflowId}/workflow_session_asset_limit_configurations`,
    ),
  postLimits: (workflowId, data) =>
    http.post(
      `/workflows/${workflowId}/workflow_session_asset_limit_configurations`,
      data,
    ),
  updateLimits: (workflowId, assetId, data) =>
    http.patch(
      `/workflows/${workflowId}/workflow_session_asset_limit_configurations/${assetId}`,
      data,
    ),
  deleteLimits: (workflowId, assetId) =>
    http.delete(
      `/workflows/${workflowId}/workflow_session_asset_limit_configurations/${assetId}`,
    ),
};

export const SessionStates = {
  getAll: () => http.get('/sessions/states'),
  getFilter: () => http.get('/filters/states'),
};

export const Widgets = {
  getAll: () => http.get('/widgets'),
  getOne: (widget, workflowType) =>
    http.get(`/widgets/${widget}?filter[workflow_type]=${workflowType}`),
};

export const WorkflowUserInterface = {
  getAll: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/user_interface`),
  updateAll: ({ workflowId, formData }) =>
    http.post(`/workflows/${workflowId}/user_interface`, formData),
  deleteById: ({ workflowId, userInterfaceId }) =>
    http.delete(`/workflows/${workflowId}/user_interface/${userInterfaceId}`),
  getPossibleOptions: () => http.get('/public/workflows/user_interface'),
};

export const EncryptionServiceApi = {
  getAvailableServices: () => http.get(`/workflow_encryption_services`),
  getServiceConfig: (wfid) =>
    http.get(`/workflows/${wfid}/encryption_services`),
  configureService: (wfid, body) =>
    http.post(`/workflows/${wfid}/encryption_services`, body),
  deleteService: (wfid) =>
    http.delete(`/workflows/${wfid}/encryption_services`),
  getServiceConfigurations: (wfid) =>
    http.get(`/workflows/${wfid}/encryption_services/configurations`),
  updateServiceConfiguration: (wfid, body) =>
    http.put(`/workflows/${wfid}/encryption_services/configurations`, body),
};

export const LegalArchivingService = {
  getAll: () => http.get('/legal_archiving_services'),
  getAssetsTypes: () => http.get('/legal_archiving_services/asset_types'),
  getWorkflowAssetsTypes: (id) =>
    http.get(`/workflows/${id}/legal_archiving_services/asset_types`),
  get: ({ workflowId, legal_archiving_service_id }) =>
    http.get(
      `/workflows/${workflowId}/legal_archiving_service_details/${legal_archiving_service_id}`,
    ),
  update: ({ workflowId, legal_archiving_service_id, data }) =>
    http.put(
      `/workflows/${workflowId}/legal_archiving_service_details/${legal_archiving_service_id}`,
      data,
    ),
};

export const SmsProvidersService = {
  getAll: () => http.get('/sms_providers'),
  get: ({ workflowId, sms_provider_service_id }) =>
    http.get(
      `/workflows/${workflowId}/sms_provider_details/${sms_provider_service_id}`,
    ),
  update: ({ workflowId, sms_provider_service_id, data }) =>
    http.put(
      `/workflows/${workflowId}/sms_provider_details/${sms_provider_service_id}`,
      data,
    ),
};

export const MicrosoftLogin = {
  loginUrl: () => http.get(`/login/microsoft`),
  loginCallback: ({ callback }) =>
    http.get(`/login/microsoft/callback?${callback}`),
};

export const OpenIdLogin = {
  loginUrl: () => http.get(`/login/openid`),
  loginCallback: ({ callback }) =>
    http.get(`/login/openid/callback?${callback}`),
};

export const Services = {
  getAll: () => http.get('/services'),
  getCervedUrl: ({ sessionId }) =>
    http.get(`/services/cerved/redirect?sid=${sessionId}`),
};

export const WorkflowPages = {
  getAll_public: ({ workflowName, configuration = {} }) =>
    http.get(`/public/workflows/${workflowName}`, merge(configuration)),
  getAll: ({ workflowId, configuration = {} }) =>
    http.get(`/workflows/${workflowId}/pages`, merge(configuration)),
  getTypes: () => http.get(`/workflow_page_types`),
  getTypesByTheme: ({ theme }) =>
    http.get(`workflow_type_themes/${theme}/workflow_page_types`),
  getOne: ({ workflowId, pageId }) =>
    http.get(`/workflows/${workflowId}/pages/${pageId}`),
  create: ({ workflowId, values }) =>
    http.post(`/workflows/${workflowId}/pages`, values),
  update: ({ workflowId, pageId, values }) =>
    http.patch(`/workflows/${workflowId}/pages/${pageId}`, values),
  delete: ({ workflowId, pageId }) =>
    http.delete(`/workflows/${workflowId}/pages/${pageId}`),
  getContent: ({ workflowId, pageId }) =>
    http.get(`/workflows/${workflowId}/pages/${pageId}/contents`),
  createContent: ({ workflowId, pageId, values }) =>
    http.post(`/workflows/${workflowId}/pages/${pageId}/contents`, values),
  updateContent: ({ workflowId, pageId, content, values }) =>
    http.patch(
      `/workflows/${workflowId}/pages/${pageId}/contents/${content}`,
      values,
    ),
  getConsent: ({ workflowId, pageId, pageConsent }) =>
    http.get(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}`,
    ),
  getConsents: ({ workflowId, pageId }) => {
    return http.get(`/workflows/${workflowId}/pages/${pageId}/consents`);
  },
  createConsent: ({ workflowId, pageId, values }) =>
    http.post(`/workflows/${workflowId}/pages/${pageId}/consents`, values),
  updateConsent: ({ workflowId, pageId, pageConsent, values }) =>
    http.patch(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}`,
      values,
    ),
  deleteConsent: ({ workflowId, pageId, pageConsent }) =>
    http.delete(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}`,
    ),
  getConsentContents: ({ workflowId, pageId, pageConsent }) =>
    http.get(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents`,
    ),
  createConsentContents: ({ workflowId, pageId, pageConsent, values }) =>
    http.post(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents`,
      values,
    ),
  getConsentContent: ({ workflowId, pageId, pageConsent, content }) =>
    http.get(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents/${content}`,
    ),
  createConsentContent: ({
    workflowId,
    pageId,
    pageConsent,
    content,
    values,
  }) =>
    http.post(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents/${content}`,
      values,
    ),
  updateConsentContent: ({
    workflowId,
    pageId,
    pageConsent,
    content,
    values,
  }) =>
    http.patch(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents/${content}`,
      values,
    ),
  deleteConsentContent: ({ workflowId, pageId, pageConsent, content }) =>
    http.delete(
      `/workflows/${workflowId}/pages/${pageId}/consents/${pageConsent}/contents/${content}`,
    ),
};

export const Statuses = {
  status: () => http.get('/public/status'),
  database: () => http.get('/public/status/database'),
  redis: () => http.get('/public/status/redis'),
  horizon: () => http.get('/public/status/horizon'),
  websocket: () => http.get('/public/status/websocket'),
  phpFpm: () => http.get('/public/status/php-fpm'),
};

export const Activities = {
  getActivityTypes: () => http.get('/sessions/activityType'),
  get: ({ sessionId, pagination = true, configuration }) =>
    http.get(
      `/sessions/${sessionId}/activities`,
      merge(configuration, {
        headers: { ...(pagination && { 'X-Pagination': true }) },
      }),
    ),
  post: ({ sessionId, data }) =>
    http.post(`/sessions/${sessionId}/activities`, data),
};

export const WorkflowEmailConfiguration = {
  get: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/email_configurations`),
  update: ({ workflowId, values }) =>
    http.put(`/workflows/${workflowId}/email_configurations`, values),
  delete: ({ workflowId, configId }) =>
    http.delete(
      `/workflows/${workflowId}/workflow_mail_configurations/${configId}`,
    ),
  updateNotificationEmails: ({ workflowId, data }) => {
    http.patch(`/workflows/${workflowId}`, data);
  },
};

export const InputTypes = {
  get: () => http.get('/input_types'),
};

export const AutocompleteItems = {
  getAutoCompleteItems: ({ workflowId }) =>
    http.get(
      `/app/workflows/${workflowId}/user_information_dropdown_keys/autocomplete`,
    ),
};

export const WorkflowFormStep = {
  addUserInfoDropdownKey: ({ workflowId, data }) =>
    http.post(`/workflows/${workflowId}/user_information_dropdown_keys`, data),
  getUserInformationDropdownKey: ({ workflowId, key }) =>
    http.get(
      `/workflows/${workflowId}/user_information_dropdown_keys?filter[key]=${key}`,
    ),
  getUserInformationDropdownKeys: (workflowId) =>
    http.get(`/workflows/${workflowId}/user_information_dropdown_keys`),
  addUserInformationDropdownKeyItem: ({ workflowId, keyId, data }) =>
    http.put(
      `/workflows/${workflowId}/user_information_dropdown_keys/${keyId}/contents`,
      data,
    ),
  deleteUserInformationDropdownKey: ({ workflowId, keyId }) =>
    http.delete(
      `/workflows/${workflowId}/user_information_dropdown_keys/${keyId}`,
    ),
  deleteUserInformationDropdownKeyItem: ({ workflowId, keyId, contentId }) =>
    http.delete(
      `/workflows/${workflowId}/user_information_dropdown_keys/${keyId}/contents/${contentId}`,
    ),
  getAllKeys: () => http.get('/sessions/user_information_keys'),
  getStepKeys: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/user_information_keys`),
  deleteKey: ({ workflowId, stepId, keyId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}`,
    ),
  postStepKey: ({ workflowId, stepId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys`,
      data,
    ),
  updateStepKey: ({ workflowId, stepId, keyId, data }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}`,
      data,
    ),
  addFilterKey: ({ workflowId, stepId, keyId, data }) =>
    http.put(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/filter`,
      data,
    ),
  deleteFilterKey: ({ workflowId, stepId, keyId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/filter`,
    ),
  getStepKeyContent: ({ workflowId, stepId, keyId }) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/contents`,
    ),
  postStepKeyContent: ({ workflowId, stepId, keyId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/contents`,
      data,
    ),
  updateStepKeyContent: ({ workflowId, stepId, keyId, contentId, data }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/contents/${contentId}`,
      data,
    ),
  postStepKeyCondition: ({ workflowId, stepId, keyId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/conditions`,
      data,
    ),
  updateStepKeyCondition: ({ workflowId, stepId, keyId, conditionId, data }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/conditions/${conditionId}`,
      data,
    ),
  deleteStepKeyCondition: ({ workflowId, stepId, keyId, conditionId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/user_information_keys/${keyId}/conditions/${conditionId}`,
    ),
};

export const AnalyticsServices = {
  getAllServices: () => http.get('/analytics_services'),
  getWorkflowAnalyticsServices: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/analytics_services`),
  postWorkflowAnalyticsServices: ({ workflowId, data }) =>
    http.put(`/workflows/${workflowId}/analytics_services`, data),
  updateWorkflowAnalyticsServiceTags: ({ workflowId, serviceId, data }) =>
    http.put(
      `/workflows/${workflowId}/analytics_service_details/${serviceId}`,
      data,
    ),
  deleteWorkflowAnalyticsService: ({ workflowId, serviceId }) =>
    http.delete(
      `/workflows/${workflowId}/analytics_service_details/${serviceId}`,
    ),
};

export const StepsAnalyticsServices = {
  getStepsAnalyticsServices: ({ workflowId, stepId, serviceId }) =>
    http.get(
      `/workflows/${workflowId}/steps/${stepId}/analytics_services/${serviceId}/tag_managements`,
    ),
  postStepsAnalyticsServices: ({ workflowId, stepId, serviceId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/analytics_services/${serviceId}/tag_managements`,
      data,
    ),
  patchStepsAnalyticsServices: ({
    workflowId,
    stepId,
    serviceId,
    tagId,
    data,
  }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/analytics_services/${serviceId}/tag_managements/${tagId}`,
      data,
    ),
  deleteStepsAnalyticsServices: ({ workflowId, stepId, serviceId, tagId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/analytics_services/${serviceId}/tag_managements/${tagId}`,
    ),
};

export const DataChecks = {
  getAllServices: () => http.get('/workflows/preregistration_checks'),
  getAllAmls: () => http.get('/workflows/aml_checks'),
  getWorkflowServices: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/preregistration_checks`),
  getWorkflowAmls: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/aml_checks`),
  postService: ({ workflowId, data }) =>
    http.post(`/workflows/${workflowId}/preregistration_checks`, data),
  postAml: ({ workflowId, data }) =>
    http.post(`/workflows/${workflowId}/aml_checks`, data),
  deleteService: ({ workflowId, id }) =>
    http.delete(`/workflows/${workflowId}/preregistration_checks/${id}`),
  deleteAml: ({ workflowId, id }) =>
    http.delete(`/workflows/${workflowId}/aml_checks/${id}`),
};

export const CartSerivces = {
  getAllServices: () => http.get('/workflow_cart_services'),
  getWorkflowServices: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/cart_services`),
  updateService: ({ workflowId, data }) =>
    http.put(`/workflows/${workflowId}/cart_services`, data),
  deleteService: ({ workflowId, id }) =>
    http.delete(`/workflows/${workflowId}/cart_services/${id}`),
};

export const DataEvaluation = {
  getAllKeys: () => http.get('/sessions/confirm_data_keys'),
  getModes: () => http.get('/sessions/confirm_data_keys/edit_modes'),
  getStepKeys: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/confirm_data_keys`),
  postStepKey: ({ workflowId, stepId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/confirm_data_keys`,
      data,
    ),
  updateStepKey: ({ workflowId, stepId, keyId, data }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/confirm_data_keys/${keyId}`,
      data,
    ),
  deleteStepKey: ({ workflowId, stepId, keyId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/confirm_data_keys/${keyId}`,
    ),
};

export const MultiUpload = {
  getTenantDocs: (tenantId) =>
    http.get(`/tenants/${tenantId}/user_document_types`),
  postTenantDocs: (tenantId, data) =>
    http.post(`/tenants/${tenantId}/user_document_types`, data),
  getTenantDocData: (tenantId, docId) =>
    http.get(`/tenants/${tenantId}/user_document_types/${docId}`),
  updateTenantDocData: (tenantId, docId, data) =>
    http.patch(`/tenants/${tenantId}/user_document_types/${docId}`, data),
  deleteTenantDocData: (tenantId, docId) =>
    http.delete(`/tenants/${tenantId}/user_document_types/${docId}`),
  getConfigs: ({ workflowId, stepId }) =>
    http.get(`/workflows/${workflowId}/steps/${stepId}/user_document_types`),
  updateConfig: ({ workflowId, stepId, data }) =>
    http.put(
      `/workflows/${workflowId}/steps/${stepId}/user_document_types`,
      data,
    ),
  deleteConfig: ({ workflowId, stepId, docId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/user_document_types/${docId}`,
    ),
  postContent: ({ workflowId, stepId, docId, data }) =>
    http.post(
      `/workflows/${workflowId}/steps/${stepId}/user_document_types/${docId}/contents`,
      data,
    ),
  updateContent: ({ workflowId, stepId, contentId, docId, data }) =>
    http.patch(
      `/workflows/${workflowId}/steps/${stepId}/user_document_types/${docId}/contents/${contentId}`,
      data,
    ),
  deleteContent: ({ workflowId, stepId, contentId, docId }) =>
    http.delete(
      `/workflows/${workflowId}/steps/${stepId}/user_document_types/${docId}/contents/${contentId}`,
    ),
};

export const TokenizationService = {
  getAllServices: () => http.get(`/detokenization_services`),
  getWorkflowTokenizationService: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/detokenization_services_configurations`),
  postConfigurations: ({ workflowId, data }) =>
    http.put(
      `/workflows/${workflowId}/detokenization_services_configurations`,
      data,
    ),
  deleteConfigurations: ({ workflowId }) =>
    http.delete(
      `/workflows/${workflowId}/detokenization_services_configurations`,
    ),
  detokenizeMobile: (sessionId) =>
    http.get(`/sessions/${sessionId}/deTokenizedMobile`),
};

export const WebhookConfigurationService = {
  get: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/webhook_parameters`),
  postConfigurations: ({ workflowId, data }) =>
    http.put(`/workflows/${workflowId}/webhook_parameters`, data),
  deleteConfigurations: ({ workflowId }) =>
    http.delete(`/workflows/${workflowId}/webhook_parameters`),
  getWebhookAdditionalAtrributes: () =>
    http.get('workflow_webhook_session_payloads'),
};

export const IdentificationService = {
  getAuthenticationKeys: () =>
    http.get(`/identification_services/authentication_keys`),
  deleteConfiguration: (wfid, serviceId) =>
    http.delete(
      `/workflows/${wfid}/identification_services_configurations/${serviceId}`,
    ),
  getSpidMismatchStates: () => http.get(`spid_data_mismatch_session_states`),
  getConfigutation: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/identification_services_configurations
  `),
  postConfiguration: ({ workflowId, data }) =>
    http.put(
      `/workflows/${workflowId}/identification_services_configurations`,
      data,
    ),
};

export const DataMatchingService = {
  getKeys: () => http.get('workflow_session_data_matching_keys'),
  getConfiguration: (workflowId) =>
    http.get(`/workflows/${workflowId}/session_data_matching_keys`),
  putConfiguration: ({ workflowId, data }) =>
    http.put(`/workflows/${workflowId}/session_data_matching_keys`, data),
  deleteConfiguration: (workflowId) =>
    http.delete(`/workflows/${workflowId}/session_data_matching_keys`),
};

export const DeviceCheckService = {
  getConfiguration: (workflowId) =>
    http.get(`/workflows/${workflowId}/device_check_configurations`),
  postConfiguration: ({ workflowId, data }) =>
    http.put(`/workflows/${workflowId}/device_check_configurations`, data),
  deleteConfiguration: (workflowId) =>
    http.delete(`/workflows/${workflowId}/device_check_configurations`),
};

export const SessionNotes = {
  getSessionNotes: ({ sessionId }) => http.get(`/sessions/${sessionId}/notes`),
  createNote: ({ sessionId, data }) =>
    http.post(`/sessions/${sessionId}/notes`, data),
};

export const OCRServices = {
  getOCRDocuments: ({ workflowId }) =>
    http.get(`/workflows/${workflowId}/soft_ocr_validation_document_types`),
  updateOCRDocuments: ({ workflowId, data }) =>
    http.put(
      `/workflows/${workflowId}/soft_ocr_validation_document_types`,
      data,
    ),
  deleteOCRDocuments: ({ workflowId }) =>
    http.delete(`/workflows/${workflowId}/soft_ocr_validation_document_types`),
};

export const DashboardGeneral = {
  getSessionVolume: (configuration = {}) =>
    http.get('/dashboard/general/session-volume', configuration),
};

export const Logout = () => http.post('/logout');
